import React, { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Typography,
  Paper,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  Button,
  IconButton,
  CircularProgress,
  Stack,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  EmailOutlined,
  Person as PersonIcon,
  Visibility,
} from "@mui/icons-material";
import JSZip from "jszip";
import { PDFDocument } from "pdf-lib";

import {
  useFindSignatureBatchById,
  useResendSignatureBatchEmail,
} from "../../../../api/documentos/endpoints";
import { useAuth } from "../../../../auth";
import { CoverDto } from "../../../../api/documentos/model/coverDto";
import { FileResponse } from "../../../../api/documentos/model/fileResponse";
import { useTranslation } from "react-i18next";
import { getEnv } from "../../../../utils/env";
import { isParaguayDomain } from "../../../../utils/envUtils";

export const Route = createFileRoute("/__auth/signatures/$id/envelope")({
  component: EnvelopeViewRoute,
});

const formatSignerDocument = (value?: string) => {
  if (!value) return "";
  if (isParaguayDomain()) return value;
  const digits = value.replace(/\D/g, "");
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return value;
};

const downloadFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.style.display = "none";
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

const fetchFileAsBlob = async (url: string) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download file from ${url}`);
  }
  return response.blob();
};

type SignatureStatusColor =
  | "default"
  | "success"
  | "error"
  | "warning"
  | "info";

const RESEND_DISABLED_STATUSES = new Set([
  "DRAFT",
  "CANCELED",
  "CANCELLED",
  "REJECTED",
  "EXPIRED",
  "SIGNED",
  "COMPLETED",
]);

export function EnvelopeViewRoute() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  const tenant = user?.tenant || "default-domain";
  const { data: envelope } = useFindSignatureBatchById(tenant, id!);
  const envelopeData = envelope as unknown as CoverDto | undefined;
  const { t } = useTranslation();

  const SIGNATURE_ENVELOPE_STATUS: Record<
    string,
    { label: string; color: SignatureStatusColor }
  > = {
    DRAFT: { label: t("signatures.status.draft"), color: "default" },
    PENDING: { label: t("signatures.status.pending"), color: "warning" },
    PROCESSING: { label: t("signatures.status.inProgress"), color: "info" },
    SIGNATURE_STARTED: {
      label: t("signatures.status.inProgress"),
      color: "info",
    },
    AWAITING_SIGNATURES: {
      label: t("signatures.awaitingSignatures"),
      color: "warning",
    },
    CANCELED: { label: t("signatures.status.canceled"), color: "error" },
    CANCELLED: { label: t("signatures.status.canceled"), color: "error" },
    REJECTED: { label: t("signatures.status.rejected"), color: "error" },
    REFUSED: { label: t("signatures.status.refused", "Recusado"), color: "error" },
    EXPIRED: { label: t("signatures.status.expired"), color: "error" },
    SIGNED: { label: t("signatures.status.signed"), color: "success" },
    COMPLETED: { label: t("signatures.status.signed"), color: "success" },
  };

  const getSignatureStatus = (status?: string) => {
    if (!status) {
      return undefined;
    }
    return (
      SIGNATURE_ENVELOPE_STATUS[status] || {
        label: status,
        color: "default" as SignatureStatusColor,
      }
    );
  };

  const [isDownloading, setIsDownloading] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const resendSignatureEmailMutation = useResendSignatureBatchEmail();

  const canResendSignatureEmails = Boolean(
    envelopeData?.signers?.length &&
      envelopeData.status &&
      !RESEND_DISABLED_STATUSES.has(envelopeData.status),
  );

  const SIGH_APP_URL =
    getEnv("VITE_APP_SMART_SIGN_APP") ||
    "https://signer-sosdocs.hml.smartdocs.com.br";

  const handleNavigateToHistory = () => {
    if (!envelopeData?.hash) return;
    const url = `${SIGH_APP_URL}/validate-envelope/${envelopeData.hash}`;
    window.open(url, "_blank");
  };

  const handleDownloadAll = async () => {
    if (!envelopeData?.files || envelopeData.files.length === 0) return;
    setIsDownloading(true);
    try {
      const zip = new JSZip();
      const promises = envelopeData.files.map(async (file: FileResponse) => {
        if (!file.temporaryUrl) return;
        const blob = await fetchFileAsBlob(file.temporaryUrl);
        const title = file.metadata?.titulo || file.filename || "documento";
        zip.file(`${title}.pdf`, blob.slice(0, blob.size, "application/pdf"));
      });

      await Promise.all(promises);
      const content = await zip.generateAsync({ type: "blob" });
      downloadFile(content, `envelope-${id}-documentos.zip`);
    } catch (error) {
      console.error("Error during zip and download process:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePrintAll = async () => {
    if (!envelopeData?.files || envelopeData.files.length === 0) return;
    setIsPrinting(true);
    try {
      const pdfBuffers = await Promise.all(
        envelopeData.files
          .filter((f) => !!f.temporaryUrl)
          .map((file) =>
            fetchFileAsBlob(file.temporaryUrl!).then((blob) =>
              blob.arrayBuffer(),
            ),
          ),
      );

      const mergedPdf = await PDFDocument.create();
      for (const pdfBuffer of pdfBuffers) {
        const pdf = await PDFDocument.load(pdfBuffer);
        const copiedPages = await mergedPdf.copyPages(
          pdf,
          pdf.getPageIndices(),
        );
        copiedPages.forEach((page) => mergedPdf.addPage(page));
      }

      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes as unknown as BlobPart], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = url;

      document.body.appendChild(iframe);

      iframe.onload = async () => {
        setTimeout(() => {
          try {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
          } catch (e) {
            console.error(
              "Could not print from iframe, falling back to download.",
              e,
            );
            downloadFile(blob, `envelope-${id}-documentos-para-impressao.pdf`);
          }
        }, 500);
      };
    } catch (error) {
      console.error("Erro durante o processo de impressão:", error);
    } finally {
      setIsPrinting(false);
    }
  };

  const handleVisualizeDocument = (url?: string) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  const handleResendSignatureEmails = () => {
    if (!canResendSignatureEmails || resendSignatureEmailMutation.isPending) {
      return;
    }

    resendSignatureEmailMutation.mutate(
      { tenantId: tenant, signatureBatchId: id },
      {
        onSuccess: () => {
          setSnackbar({
            open: true,
            message: t("signatures.resendEmailsSuccess"),
            severity: "success",
          });
        },
        onError: () => {
          setSnackbar({
            open: true,
            message: t("signatures.resendEmailsError"),
            severity: "error",
          });
        },
      },
    );
  };

  return (
    <Paper sx={{ p: 4, maxWidth: 1000, margin: "0 auto" }}>
      <Box mb={4}>
        <Typography variant="body2" color="text.secondary">
          <b>{t("signatures.envelopeId")}:</b> {id}
        </Typography>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={4} mb={4}>
        <Box flex="1 1 30%">
          <Typography variant="subtitle2" color="text.secondary">
            {t("signatures.creator")}
          </Typography>
          <Typography variant="body1">
            {envelopeData?.authorName || "-"}
          </Typography>
        </Box>
        <Box flex="1 1 30%">
          <Typography variant="subtitle2" color="text.secondary">
            {t("signatures.situation")}
          </Typography>
          <Typography variant="body1" fontWeight="500">
            {getSignatureStatus(envelopeData?.status)?.label || "-"}
          </Typography>
        </Box>
        <Box flex="1 1 30%">
          <Typography variant="subtitle2" color="text.secondary">
            {t("common.creationDate")}
          </Typography>
          <Typography variant="body1">
            {envelopeData?.createdAt
              ? new Date(envelopeData.createdAt).toLocaleString("pt-BR")
              : "-"}
          </Typography>
        </Box>
      </Box>

      <Stack direction="row" spacing={2} mb={4} flexWrap="wrap" useFlexGap>
        <Button
          variant="outlined"
          onClick={handleNavigateToHistory}
          disabled={!envelopeData?.hash}
        >
          {t("signatures.viewHistory")}
        </Button>
        <Button
          variant="outlined"
          onClick={handleDownloadAll}
          disabled={isDownloading || !envelopeData?.files?.length}
          startIcon={isDownloading ? <CircularProgress size={20} /> : undefined}
        >
          {isDownloading
            ? t("common.downloading")
            : t("signatures.downloadDocs")}
        </Button>
        <Button
          variant="outlined"
          onClick={handlePrintAll}
          disabled={isPrinting || !envelopeData?.files?.length}
          startIcon={isPrinting ? <CircularProgress size={20} /> : undefined}
        >
          {isPrinting ? t("signatures.preparing") : t("signatures.printDocs")}
        </Button>
        <Button
          variant="contained"
          onClick={handleResendSignatureEmails}
          disabled={
            !canResendSignatureEmails || resendSignatureEmailMutation.isPending
          }
          startIcon={
            resendSignatureEmailMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <EmailOutlined />
            )
          }
        >
          {resendSignatureEmailMutation.isPending
            ? t("signatures.resendingEmails")
            : t("signatures.resendEmails")}
        </Button>
      </Stack>

      <Divider sx={{ my: 4 }} />

      <Box mb={4}>
        <Typography variant="h6" gutterBottom fontWeight="500">
          {t("signatures.envelopeDocs")}
        </Typography>
        {envelopeData?.files && envelopeData.files.length > 0 ? (
          <Paper variant="outlined">
            <List disablePadding>
              {envelopeData.files.map((file, index) => (
                <React.Fragment key={file.id || index}>
                  <ListItem
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() =>
                          handleVisualizeDocument(file.temporaryUrl)
                        }
                        disabled={!file.temporaryUrl}
                        title={t("signatures.viewDocument")}
                      >
                        <Visibility />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        file.metadata?.titulo ||
                        file.filename ||
                        `Documento ${index + 1}`
                      }
                    />
                  </ListItem>
                  {index < (envelopeData.files?.length || 0) - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("signatures.noDocsListed")}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box>
        <Typography variant="h6" gutterBottom fontWeight="500">
          {t("signatures.signers")}
        </Typography>
        {envelopeData?.signers && envelopeData.signers.length > 0 ? (
          <Paper variant="outlined">
            <List disablePadding>
              {envelopeData.signers.map((signer, index) => (
                <React.Fragment key={index}>
                  <ListItem
                    secondaryAction={
                      signer.status && (
                        <Chip
                          label={
                            getSignatureStatus(signer.status)?.label ||
                            signer.status
                          }
                          size="small"
                          color={
                            getSignatureStatus(signer.status)?.color ||
                            "default"
                          }
                        />
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Avatar>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      secondaryTypographyProps={{ component: "div" }}
                      primary={
                        signer.name ||
                        signer.email ||
                        t("signatures.unknownSigner")
                      }
                      secondary={
                        <>
                          {signer.email && (
                            <Typography
                              variant="body2"
                              component="span"
                              display="block"
                            >
                              {signer.email}
                            </Typography>
                          )}
                          <Box display="flex" gap={2}>
                            {signer.document && (
                              <Typography
                                variant="body2"
                                component="span"
                                color="text.secondary"
                              >
                                {isParaguayDomain() ? "RUC/CI: " : "Doc: "}
                                {formatSignerDocument(signer.document)}
                              </Typography>
                            )}
                            {(signer as any).phoneNumber && (
                              <Typography
                                variant="body2"
                                component="span"
                                color="text.secondary"
                              >
                                {t("signatures.phone")}:{" "}
                                {(signer as any).phoneNumber}
                              </Typography>
                            )}
                            {signer.signatureOrder !== undefined && signer.signatureOrder !== null && (
                              <Typography
                                variant="body2"
                                component="span"
                                color="text.secondary"
                              >
                                {t("signatures.signatureOrder", "Ordem")}:{" "}
                                {signer.signatureOrder}
                              </Typography>
                            )}
                          </Box>
                          {(signer.refusalReason || (signer as any).motivo) && (
                            <Typography
                              variant="body2"
                              component="div"
                              color="error.main"
                              sx={{ mt: 0.5, fontStyle: "italic" }}
                            >
                              {t("signatures.refusalReason", "Motivo da recusa")}:{" "}
                              {signer.refusalReason || (signer as any).motivo}
                            </Typography>
                          )}
                          {signer.refusedAt && (
                            <Typography
                              variant="caption"
                              component="div"
                              color="text.secondary"
                              sx={{ mt: 0.25 }}
                            >
                              {t("signatures.refusalDate", "Data da recusa")}:{" "}
                              {new Date(signer.refusedAt).toLocaleString("pt-BR")}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                  {index < (envelopeData.signers?.length || 0) - 1 && (
                    <Divider />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("signatures.noSignersListed")}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 4 }} />

      <Box>
        <Typography variant="h6" gutterBottom fontWeight="500">
          {t("signatures.observers", "Observadores")}
        </Typography>
        {envelopeData?.observers && envelopeData.observers.length > 0 ? (
          <Paper variant="outlined">
            <List disablePadding>
              {envelopeData.observers.map((observer, index) => (
                <React.Fragment key={index}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <Visibility />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      secondaryTypographyProps={{ component: "div" }}
                      primary={
                        observer.name ||
                        observer.email ||
                        t("signatures.unknownObserver", "Observador")
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {observer.email && (
                            <Typography
                              variant="body2"
                              component="span"
                              display="block"
                            >
                              {observer.email}
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            component="span"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 0.5 }}
                          >
                            <b>Documentos permitidos:</b>{" "}
                            {observer.documentos && observer.documentos.length > 0
                              ? observer.documentos.join(", ")
                              : t("signatures.allDocuments", "Todos os documentos")}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < (envelopeData.observers?.length || 0) - 1 && (
                    <Divider />
                  )}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("signatures.noObserversListed", "Nenhum observador cadastrado.")}
          </Typography>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() =>
            setSnackbar((current) => ({ ...current, open: false }))
          }
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
